import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Gauge, Loader2, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConsumoRow, FeedbackStats, PLANO_LABELS, formatUsd } from './types';

interface Props {
  stats: FeedbackStats | null;
  consumos: ConsumoRow[];
  totalDisponiveis: number;
  custoMedioPorFeedback: number;
  empresaNome: (id: string | null) => string;
  usuarioNome: (id: string | null) => string;
}

interface VendaUsoRow {
  id: string;
  sessao_id: string;
  trecho_id: string | null;
  tipo: string;
  model: string;
  cost_usd: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  minutos_audio: number | null;
  created_at: string;
}

interface TrechoRow {
  id: string;
  sessao_id: string;
  origem: string;
  duracao_segundos: number | null;
  created_at: string;
}

interface SessaoRow {
  id: string;
  cliente_nome: string;
  oportunidade: string | null;
}

const DAILY_LIMIT = 5;
// Leitura pontual do workspace; o navegador não tem acesso à consulta de créditos em tempo real.
const DAILY_REMAINING = 3.8;
const QUOTA_UPDATED_AT = '23/09/26 21:42';

const FUNCTION_LABELS: Record<string, string> = {
  feedback: 'Feedbacks de treinamento',
  transcricao: 'Transcrição',
  rapport: 'Rapport e condução',
  tecnicas: 'Técnicas e objeções',
  cliente: 'Cliente e decisores',
  proposta: 'Proposta e negociação',
  velocimetro: 'Velocímetro da venda',
};

const money = (value: number) => `$${value.toFixed(6)}`;

export const FeedbacksSummaryCard = ({
  stats,
  consumos,
  totalDisponiveis,
  custoMedioPorFeedback,
  empresaNome,
  usuarioNome,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [vendaUsos, setVendaUsos] = useState<VendaUsoRow[]>([]);
  const [trechos, setTrechos] = useState<TrechoRow[]>([]);
  const [sessoes, setSessoes] = useState<Record<string, SessaoRow>>({});
  const [usageLoaded, setUsageLoaded] = useState(false);

  const atual = stats?.mesAtual ?? 0;
  const anterior = stats?.mesAnterior ?? 0;
  const pct = anterior === 0 ? 100 : Math.round(((atual - anterior) / anterior) * 100);
  const up = atual >= anterior;

  useEffect(() => {
    if (!open || usageLoaded) return;

    const loadUsage = async () => {
      setLoadingUsage(true);
      const [usosResult, trechosResult, sessoesResult] = await Promise.all([
        (supabase as any)
          .from('venda_uso_ia')
          .select('id, sessao_id, trecho_id, tipo, model, cost_usd, input_tokens, output_tokens, minutos_audio, created_at')
          .order('created_at', { ascending: false })
          .limit(1000),
        (supabase as any)
          .from('venda_trechos')
          .select('id, sessao_id, origem, duracao_segundos, created_at')
          .order('created_at', { ascending: false })
          .limit(500),
        (supabase as any)
          .from('venda_sessoes')
          .select('id, cliente_nome, oportunidade'),
      ]);

      if (usosResult.error || trechosResult.error || sessoesResult.error) {
        console.error('Erro ao carregar consumo do Auxiliar de Venda:', {
          usos: usosResult.error,
          trechos: trechosResult.error,
          sessoes: sessoesResult.error,
        });
      }

      setVendaUsos((usosResult.data || []) as VendaUsoRow[]);
      setTrechos((trechosResult.data || []) as TrechoRow[]);
      const sessionMap: Record<string, SessaoRow> = {};
      ((sessoesResult.data || []) as SessaoRow[]).forEach((sessao) => {
        sessionMap[sessao.id] = sessao;
      });
      setSessoes(sessionMap);
      setUsageLoaded(true);
      setLoadingUsage(false);
    };

    loadUsage();
  }, [open, usageLoaded]);

  const groupedSegments = useMemo(() => {
    const usageBySegment = new Map<string, VendaUsoRow[]>();
    vendaUsos.forEach((uso) => {
      if (!uso.trecho_id) return;
      const rows = usageBySegment.get(uso.trecho_id) || [];
      rows.push(uso);
      usageBySegment.set(uso.trecho_id, rows);
    });

    const unlinkedTranscriptions = vendaUsos.filter(
      (uso) => uso.tipo === 'transcricao' && !uso.trecho_id,
    );
    const usedTranscriptions = new Set<string>();

    return trechos
      .map((trecho) => {
        const processing = usageBySegment.get(trecho.id) || [];
        const trechoTime = new Date(trecho.created_at).getTime();
        const transcription = unlinkedTranscriptions
          .filter((uso) => {
            if (usedTranscriptions.has(uso.id) || uso.sessao_id !== trecho.sessao_id) return false;
            const difference = trechoTime - new Date(uso.created_at).getTime();
            return difference >= 0 && difference <= 10 * 60 * 1000;
          })
          .sort(
            (a, b) =>
              Math.abs(trechoTime - new Date(a.created_at).getTime()) -
              Math.abs(trechoTime - new Date(b.created_at).getTime()),
          )[0];

        if (transcription) usedTranscriptions.add(transcription.id);
        const transcriptionCost = Number(transcription?.cost_usd) || 0;
        const processingCost = processing.reduce((sum, uso) => sum + (Number(uso.cost_usd) || 0), 0);
        const models = Array.from(
          new Set([transcription?.model, ...processing.map((uso) => uso.model)].filter(Boolean)),
        );

        return {
          ...trecho,
          sessao: sessoes[trecho.sessao_id],
          transcriptionCost,
          processingCost,
          totalCost: transcriptionCost + processingCost,
          processingTypes: processing.map((uso) => uso.tipo),
          models,
          minutosAudio: Number(transcription?.minutos_audio) || (trecho.duracao_segundos || 0) / 60,
        };
      })
      .filter((trecho) => trecho.totalCost > 0 || trecho.processingTypes.length > 0);
  }, [sessoes, trechos, vendaUsos]);

  const dailyUsage = useMemo(() => {
    const today = new Date().toLocaleDateString('en-CA');
    const totals = new Map<string, { requests: number; cost: number }>();
    const add = (key: string, cost: number) => {
      const current = totals.get(key) || { requests: 0, cost: 0 };
      totals.set(key, { requests: current.requests + 1, cost: current.cost + cost });
    };

    consumos.forEach((consumo) => {
      if (new Date(consumo.created_at).toLocaleDateString('en-CA') === today) {
        add('feedback', Number(consumo.cost_usd) || 0);
      }
    });
    vendaUsos.forEach((uso) => {
      if (new Date(uso.created_at).toLocaleDateString('en-CA') === today) {
        add(uso.tipo, Number(uso.cost_usd) || 0);
      }
    });

    return Array.from(totals.entries())
      .map(([key, value]) => ({ key, label: FUNCTION_LABELS[key] || key, ...value }))
      .sort((a, b) => b.cost - a.cost);
  }, [consumos, vendaUsos]);

  return (
    <>
      <Card className="cursor-pointer transition-shadow hover:shadow-lg" onClick={() => setOpen(true)}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Feedbacks e consumo
          </CardTitle>
          <CardDescription>Clique para ver custos e a última consulta da cota diária</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold">{atual}</p>
            {anterior === 0 && atual === 0 ? (
              <span className="text-xs text-muted-foreground">sem histórico</span>
            ) : (
              <span className={`flex items-center gap-1 text-xs font-medium ${up ? 'text-emerald-600' : 'text-destructive'}`}>
                {up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                {Math.abs(pct)}%
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{anterior} no mês anterior</p>
          <p className="mt-2 text-xs text-muted-foreground">
            {stats?.total ?? 0} desde o início · {totalDisponiveis} disponíveis
          </p>
          <p className="text-xs text-muted-foreground">
            Custo: {formatUsd(stats?.custoMesAtual ?? 0)} no mês · {formatUsd(stats?.custoTotal ?? 0)} total
          </p>
          <div className="mt-3 border-t pt-3">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium">Cota diária · última consulta</span>
              <span>{DAILY_REMAINING.toFixed(2).replace('.', ',')} de {DAILY_LIMIT.toFixed(0)} créditos</span>
            </div>
            <Progress value={(DAILY_REMAINING / DAILY_LIMIT) * 100} className="mt-1 h-2" />
            <p className="mt-1 text-xs text-muted-foreground">Consultada em {QUOTA_UPDATED_AT}</p>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] max-w-6xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Consumo de inteligência artificial</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="auxiliar" className="w-full">
            <TabsList className="grid h-auto w-full grid-cols-3">
              <TabsTrigger value="feedbacks">Feedbacks</TabsTrigger>
              <TabsTrigger value="auxiliar">Por cliente e trecho</TabsTrigger>
              <TabsTrigger value="diario">Consumo diário</TabsTrigger>
            </TabsList>

            <TabsContent value="feedbacks" className="overflow-x-auto">
              {consumos.length > 0 ? (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Data</TableHead><TableHead>Empresa</TableHead><TableHead>Usuário</TableHead>
                    <TableHead>Persona</TableHead><TableHead>Plano</TableHead><TableHead className="text-right">Custo (USD)</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{consumos.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="whitespace-nowrap">{new Date(c.created_at).toLocaleString('pt-BR')}</TableCell>
                      <TableCell>{empresaNome(c.empresa_id)}</TableCell><TableCell>{usuarioNome(c.user_id)}</TableCell>
                      <TableCell className="font-mono text-xs">{c.persona_id ? c.persona_id.slice(0, 8) : '—'}</TableCell>
                      <TableCell>{c.plano ? PLANO_LABELS[c.plano] : '—'}</TableCell>
                      <TableCell className="text-right">{money(Number(c.cost_usd) || 0)}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              ) : <div className="py-8 text-center text-muted-foreground">Nenhum feedback consumido ainda.</div>}
            </TabsContent>

            <TabsContent value="auxiliar" className="overflow-x-auto">
              {loadingUsage ? (
                <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" /> Carregando consumo
                </div>
              ) : groupedSegments.length > 0 ? (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead>Trecho</TableHead>
                    <TableHead>Processamentos</TableHead><TableHead className="text-right">Transcrição</TableHead>
                    <TableHead className="text-right">Processamento</TableHead><TableHead className="text-right">Total</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{groupedSegments.map((trecho) => (
                    <TableRow key={trecho.id}>
                      <TableCell className="whitespace-nowrap text-xs">{new Date(trecho.created_at).toLocaleString('pt-BR')}</TableCell>
                      <TableCell>
                        <p className="font-medium">{trecho.sessao?.cliente_nome || 'Cliente não identificado'}</p>
                        <p className="text-xs text-muted-foreground">{trecho.sessao?.oportunidade || trecho.sessao_id.slice(0, 8)}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{trecho.origem === 'audio' ? 'Áudio' : 'Texto'}</Badge>
                        {trecho.minutosAudio > 0 && <p className="mt-1 text-xs text-muted-foreground">{trecho.minutosAudio.toFixed(1)} min</p>}
                      </TableCell>
                      <TableCell className="max-w-[260px]">
                        <p className="text-xs">{trecho.processingTypes.map((type) => FUNCTION_LABELS[type] || type).join(' · ') || '—'}</p>
                        <p className="mt-1 truncate text-xs text-muted-foreground" title={trecho.models.join(', ')}>{trecho.models.join(', ')}</p>
                      </TableCell>
                      <TableCell className="text-right">{money(trecho.transcriptionCost)}</TableCell>
                      <TableCell className="text-right">{money(trecho.processingCost)}</TableCell>
                      <TableCell className="text-right font-semibold">{money(trecho.totalCost)}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              ) : <div className="py-8 text-center text-muted-foreground">Nenhum consumo por trecho encontrado.</div>}
            </TabsContent>

            <TabsContent value="diario" className="space-y-5 pt-3">
              <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
                <div className="rounded-md border p-4">
                  <div className="flex items-center gap-2"><Gauge className="h-5 w-5 text-primary" /><p className="font-semibold">Cota diária do workspace</p></div>
                  <p className="mt-3 text-3xl font-bold">{DAILY_REMAINING.toFixed(2).replace('.', ',')}</p>
                  <p className="text-sm text-muted-foreground">de {DAILY_LIMIT} créditos restantes</p>
                  <Progress value={(DAILY_REMAINING / DAILY_LIMIT) * 100} className="mt-4 h-2" />
                  <p className="mt-2 text-xs text-muted-foreground">Última consulta: {QUOTA_UPDATED_AT}. Este número não atualiza automaticamente; a cota renova diariamente.</p>
                </div>
                <div className="overflow-hidden rounded-md border">
                  <Table>
                    <TableHeader><TableRow><TableHead>Funcionalidade usada hoje</TableHead><TableHead>Requisições</TableHead><TableHead className="text-right">Custo estimado (USD)</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {dailyUsage.map((item) => <TableRow key={item.key}><TableCell>{item.label}</TableCell><TableCell>{item.requests}</TableCell><TableCell className="text-right font-medium">{money(item.cost)}</TableCell></TableRow>)}
                      {!dailyUsage.length && <TableRow><TableCell colSpan={3} className="py-8 text-center text-muted-foreground">Nenhum consumo registrado hoje.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Os custos em dólar vêm dos registros de cada função. Créditos do workspace e custos estimados em dólar são medidas diferentes.</p>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};